import type {
  CarsListSuccess,
} from '@autobid/strapi-integration/typescript/auction/carsList'
import type {
  AuctionCarsApiFilters,
} from '@autobid/ui/composables/car/data/useAuctionCars'
import type { CarFinishedEvent } from '@autobid/ui/types/websocket/CarFinishedEvent'
import type { QueryKey } from '@tanstack/vue-query'
import { useWebSocketStore } from '@autobid/nuxt-pinia-store/store/useWebSocketStore'
import {
  auctionCarsQueryKey,
} from '@autobid/ui/composables/car/data/useAuctionCars'
import { AUCTION_CARS_SOCKET_CALLBACK } from '@autobid/ui/composables/car/sockets/useAuctionCarsSocket'
import { useUpdateCar } from '@autobid/ui/composables/car/useUpdateCar'
import { useQueryClient } from '@tanstack/vue-query'

export function useAuctionCarsCarFinished() {
  const { startListen } = useWebSocketStore()
  const queryClient = useQueryClient()
  const { updateAuctionCar } = useUpdateCar()

  const carFinished = () => {
    const updateFinishedCarData = (event: CarFinishedEvent) => {
      const { auctionId, carId, itemStatus } = event.context

      updateAuctionCar(auctionId, carId, (data) => {
        return {
          ...data,
          state: itemStatus,
          stage: 'FINISHED',
          inActiveHotbidPhase: false,
        }
      })
    }

    const prepareNextCar = (event: CarFinishedEvent) => {
      const { auctionId, nextId, nextHotbidStart } = event.context

      if (!nextId)
        return

      updateAuctionCar(auctionId, nextId, (data) => {
        return {
          ...data,
          hotbidStartTime: new Date(nextHotbidStart).toISOString(),
          stage: 'IN_AUCTION',
        }
      })
    }

    const removeFinishedCarFromActiveInAuctionQuery = (event: CarFinishedEvent, activeQueryKey: QueryKey) => {
      const { carId } = event.context
      const currentData = queryClient.getQueryData<CarsListSuccess['data']['items']>(activeQueryKey)

      if (!currentData)
        return

      if (currentData.items.length === 1) {
        // last car in the list - invalidate the query
        queryClient.invalidateQueries(activeQueryKey)
        return
      }

      queryClient.setQueryData(
        activeQueryKey,
        (data?: CarsListSuccess['data']['items']): CarsListSuccess['data']['items'] | undefined => {
          if (!data) {
            return data
          }

          return {
            ...data,
            items: data.items.filter(el => el.id !== carId),
          }
        },
      )

      queryClient.invalidateQueries({
        queryKey: activeQueryKey,
        refetchType: 'none',
      })
    }

    const invalidateCars = (event: CarFinishedEvent) => {
      const { carId, auctionId } = event.context
      let activeInAuctionQueryKey: QueryKey | undefined

      queryClient.invalidateQueries({
        refetchType: 'none',
        queryKey: auctionCarsQueryKey.byAuctionId(auctionId),
        predicate: (query) => {
          const queryData = query.state.data as CarsListSuccess['data']['items'] | undefined
          if (!queryData) {
            return true
          }

          const filters = query.queryKey[2] as AuctionCarsApiFilters
          const filteredByInAuction = filters.stage === 'online'

          if (filteredByInAuction) {
            const containsTheGivenCar = !!queryData.items.find(
              item => item.id === carId,
            )

            if (containsTheGivenCar && query.isActive()) {
              activeInAuctionQueryKey = query.queryKey
            }
          }

          return true
        },
      })

      activeInAuctionQueryKey && removeFinishedCarFromActiveInAuctionQuery(event, activeInAuctionQueryKey)
    }

    startListen({
      eventName: 'premium.CAR_FINISHED',
      callbackName: AUCTION_CARS_SOCKET_CALLBACK,
      callback: (event) => {
        /*
          courier sends double finish events, one of them is invalid and it will be fixed soon,
          the invalid event has input set to "premium"
        */
        if (event.input === 'premium')
          return

        updateFinishedCarData(event)
        prepareNextCar(event)
        invalidateCars(event)
      },
    })
  }

  return { carFinished }
}
